import { searchRationalRoot } from "@/lib/tool-kit/algebra/polynomial";
import {
  integerContent,
  type Uni,
  uniCoefficient,
  uniCommonDenominator,
  uniDegree,
  uniDivmod,
  uniEquals,
  uniLeading,
  uniMul,
  uniPow,
  uniScale,
  uniTrim,
} from "@/lib/tool-kit/algebra/uni";
import {
  absBig,
  BIG_ONE,
  BIG_ZERO,
  gcdBig,
  isqrtBig,
} from "@/lib/tool-kit/math/bigint";
import {
  equalsR,
  fromBigInt,
  intR,
  isZeroR,
  makeRational,
  RATIONAL_ZERO,
  type Rational,
  signR,
} from "@/lib/tool-kit/math/rational";

/**
 * Factoring a one-variable polynomial over the rationals, with the working recorded.
 *
 * The order is the one a teacher uses: take out the greatest common factor first, then
 * try the special patterns (difference of squares, sum and difference of cubes, perfect
 * square trinomial), the AC method for quadratics, grouping for four terms, and the
 * rational root theorem for anything else. Each success is recorded as a step; every
 * factor is kept primitive (whole-number coefficients, positive leading coefficient).
 */
export type FactorStep =
  | { kind: "gcf"; poly: Uni; gcf: Uni; remaining: Uni; negative: boolean }
  | { kind: "difference-squares"; poly: Uni; first: Uni; second: bigint }
  | { kind: "perfect-square"; poly: Uni; root: Uni }
  | {
      kind: "sum-cubes" | "difference-cubes";
      poly: Uni;
      first: Uni;
      second: bigint;
      trinomial: Uni;
    }
  | {
      kind: "ac";
      poly: Uni;
      product: bigint;
      m: bigint;
      n: bigint;
      g1: bigint;
      g2: bigint;
      binomial: Uni;
    }
  | {
      kind: "grouping";
      poly: Uni;
      g1: Uni;
      g2: Uni;
      binomial: Uni;
      other: Uni;
    }
  | {
      kind: "rational-root";
      poly: Uni;
      root: Rational;
      factor: Uni;
      quotient: Uni;
    }
  | { kind: "quadratic-pair"; poly: Uni; first: Uni; second: Uni }
  | { kind: "irreducible"; poly: Uni; certain: boolean };

export interface Factor {
  poly: Uni;
  exponent: number;
}

export interface FactorResult {
  /** The numeric factor in front (may be negative or a fraction). */
  constant: Rational;
  /** The power of the variable taken out: x^k. */
  xPower: number;
  factors: Factor[];
  steps: FactorStep[];
  /** Expanding the factors reproduces the input. */
  verified: boolean;
  /** False when a factor of degree 4 or more was left because no method split it. */
  complete: boolean;
}

const ONE = intR(1);

function big(n: bigint): Rational {
  return fromBigInt(n);
}

function monomial(coefficient: bigint, degree: number): Uni {
  return [
    ...Array.from({ length: degree }, () => RATIONAL_ZERO),
    big(coefficient),
  ];
}

function icbrt(n: bigint): bigint {
  let low = BIG_ZERO;
  let high = BIG_ONE;
  while (high * high * high <= n) {
    high *= BigInt(2);
  }
  while (low < high) {
    const mid = (low + high + BIG_ONE) / BigInt(2);
    if (mid * mid * mid <= n) {
      low = mid;
    } else {
      high = mid - BIG_ONE;
    }
  }
  return low;
}

function isSquare(n: bigint): boolean {
  if (n < BIG_ZERO) {
    return false;
  }
  const r = isqrtBig(n);
  return r * r === n;
}

function termsOf(p: Uni): number[] {
  const degrees: number[] = [];
  p.forEach((c, i) => {
    if (!isZeroR(c)) {
      degrees.push(i);
    }
  });
  return degrees;
}

function num(p: Uni, degree: number): bigint {
  return uniCoefficient(p, degree).num;
}

interface Context {
  steps: FactorStep[];
  complete: boolean;
  /** Every search for a rational root ran to the end. */
  exhaustive: boolean;
}

/** A primitive polynomial with positive leading coefficient, from any integer one. */
function primitive(p: Uni): Uni {
  const scaled = uniScale(p, makeRational(BIG_ONE, integerContent(p)));
  return signR(uniLeading(scaled)) < 0 ? uniScale(scaled, intR(-1)) : scaled;
}

function binomialPattern(p: Uni, ctx: Context): Uni[] | null {
  const degrees = termsOf(p);
  if (degrees.length !== 2 || degrees[0] !== 0) {
    return null;
  }
  const n = degrees[1];
  const a = num(p, n);
  const c = num(p, 0);
  if (n % 2 === 0 && isSquare(a) && isSquare(-c)) {
    const alpha = isqrtBig(a);
    const beta = isqrtBig(-c);
    const m = n / 2;
    const minus = uniAddConstant(monomial(alpha, m), -beta);
    const plus = uniAddConstant(monomial(alpha, m), beta);
    ctx.steps.push({
      kind: "difference-squares",
      poly: p,
      first: monomial(alpha, m),
      second: beta,
    });
    return [minus, plus];
  }
  if (n % 3 === 0) {
    return cubePattern(p, n / 3, a, c, ctx);
  }
  return null;
}

function uniAddConstant(p: Uni, constant: bigint): Uni {
  const result = [...p];
  result[0] = big(constant + uniCoefficient(p, 0).num);
  return uniTrim(result);
}

function cubePattern(
  p: Uni,
  m: number,
  a: bigint,
  c: bigint,
  ctx: Context
): Uni[] | null {
  const alpha = icbrt(a);
  const beta = icbrt(absBig(c));
  if (alpha ** BigInt(3) !== a || beta ** BigInt(3) !== absBig(c)) {
    return null;
  }
  const sum = c > BIG_ZERO;
  const first = uniAddConstant(monomial(alpha, m), sum ? beta : -beta);
  const middle = sum ? -alpha * beta : alpha * beta;
  const trinomial: Uni = [];
  trinomial[0] = big(beta * beta);
  for (let i = 1; i < 2 * m; i += 1) {
    trinomial[i] = RATIONAL_ZERO;
  }
  trinomial[m] = big(middle);
  trinomial[2 * m] = big(alpha * alpha);
  const fixed = uniTrim(trinomial.map((v) => v ?? RATIONAL_ZERO));
  ctx.steps.push({
    kind: sum ? "sum-cubes" : "difference-cubes",
    poly: p,
    first: monomial(alpha, m),
    second: beta,
    trinomial: fixed,
  });
  return [first, fixed];
}

/** ax² + bx + c, primitive: perfect square, AC method, or irreducible. */
function quadratic(p: Uni, ctx: Context): Uni[] | null {
  const a = num(p, 2);
  const b = num(p, 1);
  const c = num(p, 0);
  const disc = b * b - BigInt(4) * a * c;
  if (!isSquare(disc)) {
    ctx.steps.push({ kind: "irreducible", poly: p, certain: true });
    return null;
  }
  const s = isqrtBig(disc);
  if (s === BIG_ZERO) {
    const alpha = isqrtBig(a);
    const root: Uni = [
      big(b > BIG_ZERO ? isqrtBig(c) : -isqrtBig(c)),
      big(alpha),
    ];
    ctx.steps.push({ kind: "perfect-square", poly: p, root });
    return [root, root];
  }
  const m = (b - s) / BigInt(2);
  const n = (b + s) / BigInt(2);
  const g1 = gcdBig(a, absBig(m));
  const first: Uni = [big(m / g1), big(a / g1)];
  const g2 = n / (a / g1);
  ctx.steps.push({
    kind: "ac",
    poly: p,
    product: a * c,
    m,
    n,
    g1,
    g2,
    binomial: first,
  });
  const second: Uni = [big(g2), big(g1)];
  return [first, second];
}

function gcfOfPair(p: Uni): { gcf: Uni; rest: Uni } {
  const content = integerContent(p);
  const low = termsOf(p)[0] ?? 0;
  const gcf = uniMul([big(content)], monomial(BIG_ONE, low));
  const quotient = uniDivmod(p, gcf).quotient;
  return { gcf, rest: quotient };
}

function groupingPattern(p: Uni, ctx: Context): Uni[] | null {
  if (termsOf(p).length !== 4 || uniDegree(p) !== 3) {
    return null;
  }
  const top = gcfOfPair(uniTrim([RATIONAL_ZERO, RATIONAL_ZERO, p[2], p[3]]));
  let bottom = gcfOfPair(uniTrim([p[0], p[1]]));
  // Take a negative out of the second pair if that is what makes the brackets match.
  if (!uniEquals(top.rest, bottom.rest)) {
    if (!uniEquals(top.rest, uniScale(bottom.rest, intR(-1)))) {
      return null;
    }
    bottom = { gcf: uniScale(bottom.gcf, intR(-1)), rest: top.rest };
  }
  const other = uniTrim(addPolys(top.gcf, bottom.gcf));
  ctx.steps.push({
    kind: "grouping",
    poly: p,
    g1: top.gcf,
    g2: bottom.gcf,
    binomial: top.rest,
    other,
  });
  return [top.rest, other];
}

function addPolys(a: Uni, b: Uni): Uni {
  const length = Math.max(a.length, b.length);
  const out: Uni = [];
  for (let i = 0; i < length; i += 1) {
    out.push(addRat(uniCoefficient(a, i), uniCoefficient(b, i)));
  }
  return out;
}

function addRat(a: Rational, b: Rational): Rational {
  return makeRational(a.num * b.den + b.num * a.den, a.den * b.den);
}

function divisors(n: bigint): bigint[] | null {
  const value = absBig(n);
  if (value > BigInt(1_000_000_000_000)) {
    return null;
  }
  const limit = Number(value);
  const found: bigint[] = [];
  for (let d = 1; d * d <= limit; d += 1) {
    if (limit % d === 0) {
      found.push(BigInt(d));
      if (d * d !== limit) {
        found.push(BigInt(limit / d));
      }
    }
  }
  return found;
}

function exactDivide(n: bigint, d: bigint): bigint | null {
  return n % d === BIG_ZERO ? n / d : null;
}

function solvePair(
  [a1, a2]: [bigint, bigint],
  [e1, e2]: [bigint, bigint],
  coefficients: { b: bigint; c: bigint; d: bigint }
): [bigint, bigint] | null {
  const det = a2 * e1 - a1 * e2;
  if (det === BIG_ZERO) {
    return solveSingular([a1, a2], [e1, e2], coefficients);
  }
  const p = exactDivide(coefficients.b * e1 - a1 * coefficients.d, det);
  const q = exactDivide(a2 * coefficients.d - e2 * coefficients.b, det);
  if (p === null || q === null) {
    return null;
  }
  return a1 * e2 + a2 * e1 + p * q === coefficients.c ? [p, q] : null;
}

/**
 * The two linear equations for the x-coefficients are dependent when the constant terms
 * are in the same ratio as the leading ones (as in x⁴ + 4). The x² coefficient then pins
 * the pair down through a quadratic in a₂p.
 */
function solveSingular(
  [a1, a2]: [bigint, bigint],
  [e1, e2]: [bigint, bigint],
  { b, c, d }: { b: bigint; c: bigint; d: bigint }
): [bigint, bigint] | null {
  const k = c - a1 * e2 - a2 * e1;
  const disc = b * b - BigInt(4) * a1 * a2 * k;
  if (!isSquare(disc)) {
    return null;
  }
  const s = isqrtBig(disc);
  for (const u of [(b + s) / BigInt(2), (b - s) / BigInt(2)]) {
    const p = exactDivide(u, a2);
    const q = exactDivide(b - u, a1);
    if (
      p !== null &&
      q !== null &&
      (b + s) % BigInt(2) === BIG_ZERO &&
      p * e2 + q * e1 === d
    ) {
      return [p, q];
    }
  }
  return null;
}

/** A quartic as a product of two integer quadratics, or null. */
function quadraticPair(p: Uni, ctx: Context): Uni[] | null {
  if (uniDegree(p) !== 4) {
    return null;
  }
  const lead = divisors(num(p, 4));
  const tail = divisors(num(p, 0));
  if (lead === null || tail === null || lead.length * tail.length > 4000) {
    return null;
  }
  const coefficients = { b: num(p, 3), c: num(p, 2), d: num(p, 1) };
  for (const a1 of lead) {
    const a2 = num(p, 4) / a1;
    for (const e1 of tail) {
      for (const sign of [BIG_ONE, -BIG_ONE]) {
        const first = e1 * sign;
        const second = num(p, 0) / first;
        const solved = solvePair([a1, a2], [first, second], coefficients);
        if (solved) {
          const f1: Uni = [big(first), big(solved[0]), big(a1)];
          const f2: Uni = [big(second), big(solved[1]), big(a2)];
          ctx.steps.push({
            kind: "quadratic-pair",
            poly: p,
            first: f1,
            second: f2,
          });
          return [f1, f2];
        }
      }
    }
  }
  return null;
}

function viaRationalRoot(p: Uni, ctx: Context): Uni[] | null {
  const { root, exhaustive } = searchRationalRoot(p);
  if (root === null) {
    ctx.exhaustive = ctx.exhaustive && exhaustive;
    return null;
  }
  const factor: Uni = [big(-root.num), big(root.den)];
  const quotient = uniDivmod(p, factor).quotient;
  ctx.steps.push({ kind: "rational-root", poly: p, root, factor, quotient });
  return [factor, primitive(quotient)];
}

/** Split `p` (primitive, positive lead) into irreducible factors, recording each step. */
function factorPrimitive(p: Uni, ctx: Context): Uni[] {
  if (uniDegree(p) <= 1) {
    return [p];
  }
  const pieces =
    binomialPattern(p, ctx) ??
    (uniDegree(p) === 2 ? quadratic(p, ctx) : null) ??
    groupingPattern(p, ctx) ??
    viaRationalRoot(p, ctx) ??
    quadraticPair(p, ctx);
  if (pieces === null) {
    if (uniDegree(p) > 2) {
      ctx.steps.push({
        kind: "irreducible",
        poly: p,
        certain: uniDegree(p) === 3,
      });
      ctx.complete = ctx.complete && uniDegree(p) === 3;
    }
    return [p];
  }
  if (pieces.length === 1) {
    return [p];
  }
  return pieces.flatMap((piece) =>
    uniEquals(piece, p) ? [piece] : factorPrimitive(primitive(piece), ctx)
  );
}

function collect(factors: Uni[]): Factor[] {
  const result: Factor[] = [];
  for (const factor of factors) {
    const known = result.find((f) => uniEquals(f.poly, factor));
    if (known) {
      known.exponent += 1;
    } else {
      result.push({ poly: factor, exponent: 1 });
    }
  }
  return result.sort(
    (a, b) =>
      uniDegree(a.poly) - uniDegree(b.poly) || compareLowFirst(a.poly, b.poly)
  );
}

function compareLowFirst(a: Uni, b: Uni): number {
  for (let i = 0; i < Math.max(a.length, b.length); i += 1) {
    const x = uniCoefficient(a, i);
    const y = uniCoefficient(b, i);
    if (!equalsR(x, y)) {
      return Number(x.num * y.den - y.num * x.den);
    }
  }
  return 0;
}

/** The product of the pieces, for the verification. */
export function expandFactorization(result: FactorResult): Uni {
  let total: Uni = [result.constant];
  if (result.xPower > 0) {
    total = uniMul(total, monomial(BIG_ONE, result.xPower));
  }
  for (const factor of result.factors) {
    total = uniMul(total, uniPow(factor.poly, factor.exponent));
  }
  return uniTrim(total);
}

/** Factor a non-zero polynomial. Returns null for the zero polynomial. */
export function factorPolynomial(p: Uni): FactorResult | null {
  const input = uniTrim(p);
  if (input.length === 0) {
    return null;
  }
  const steps: FactorStep[] = [];
  const ctx: Context = { steps, complete: true, exhaustive: true };
  const denominator = uniCommonDenominator([input]);
  const integers = uniScale(input, big(denominator));
  const content = integerContent(integers);
  const low = termsOf(integers)[0] ?? 0;
  const negative = signR(uniLeading(integers)) < 0;
  const constant = makeRational(negative ? -content : content, denominator);
  const rest = uniDivmod(
    uniScale(integers, makeRational(negative ? -BIG_ONE : BIG_ONE, content)),
    monomial(BIG_ONE, low)
  ).quotient;
  const hasGcf = !equalsR(constant, ONE) || low > 0;
  if (hasGcf && uniDegree(input) > 0) {
    steps.push({
      kind: "gcf",
      poly: input,
      gcf: uniMul([constant], monomial(BIG_ONE, low)),
      remaining: rest,
      negative,
    });
  }
  const factors = collect(factorPrimitive(rest, ctx));
  const result: FactorResult = {
    constant,
    xPower: low,
    factors: factors.filter((f) => uniDegree(f.poly) > 0),
    steps,
    verified: false,
    complete: ctx.complete,
  };
  result.verified = uniEquals(expandFactorization(result), input);
  return result;
}
